import { useState, useCallback, useRef, useEffect } from 'react';
import useProductTypeLabels from '@hooks/useProductTypeLabels';
import useConfig from '@hooks/useConfig';
import { isAdjustableRate, isFixedRate, isSSR, camelCaseProps } from '@helpers';
import { loanPurpose, closingCostType } from '@enums';
import { RACK_RATES_ENDPOINT } from '@constants';
import { round } from 'lodash';

export default function useRackRates(
  forceShowRackRates,
  initialData = [],
  productTypes,
  defaultScenario
) {
  const config = useConfig();
  const [showRackRates, setShowRackRates] = useState(!!forceShowRackRates);
  const labels = useProductTypeLabels(productTypes);
  const [rackRateGroups, setRackRateGroups] = useState(
    formatRackRateGroups(initialData, labels.current, defaultScenario)
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadingTimer = useRef(null);
  useEffect(() => clearTimeout(loadingTimer.current), []);

  const fetchRackRateGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      setShowRackRates(true);
      clearTimeout(loadingTimer.current);

      // Ensure loading spinner displays to
      // avoid awkward layout shift
      const minLoadTime = 600;
      const start = performance.now();

      const response = await fetch(RACK_RATES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip, deflate',
          Accept: '*/*'
        }
      });

      const data = camelCaseProps(await response.json());
      const groups = formatRackRateGroups(
        data,
        labels.current,
        defaultScenario
      );

      const end = performance.now();
      const delay = Math.max(0, minLoadTime - (end - start));

      loadingTimer.current = setTimeout(() => {
        setRackRateGroups(groups);
        setIsLoading(false);
      }, Math.round(delay));
    } catch (error) {
      clearTimeout(loadingTimer.current);
      console.error(error);

      setIsLoading(false);
      setError(config.rackRatesErrorMessage);
    }
  }, [labels, defaultScenario, config.rackRatesErrorMessage]);

  const [rackRateLoanPurpose, setRackRateLoanPurpose] = useState(
    defaultScenario.loanPurpose ?? loanPurpose.REFINANCE
  );
  const [rackRateLoanType, setRackRateLoanType] = useState(closingCostType.NCC);

  if (!isSSR()) {
    window.getRackRates = () => rackRateGroups;
  }

  return {
    showRackRates,
    setShowRackRates,
    rackRateGroups,
    rackRateLoanPurpose,
    rackRateLoanType,
    error,
    isLoading,
    setRackRateLoanPurpose,
    setRackRateLoanType,
    fetchRackRateGroups
  };
}

function formatRackRateGroups(groups, labels, defaultScenario) {
  if (!Array.isArray(groups)) {
    return [];
  }

  return groups.map(group => {
    group.loanPurpose = group.loanPurpose?.trim().toLowerCase();
    group.nccRates = mapRates(group.nccRates, labels, defaultScenario);
    group.ptsFeesRates = mapRates(group.ptsFeesRates, labels, defaultScenario);

    return group;
  });
}

function mapRates(rates, labels, defaultScenario) {
  if (!Array.isArray(rates)) {
    return [];
  }

  return rates
    .map(r => formatRackRate(r, labels, defaultScenario))
    .filter(r => r);
}

function formatRackRate(rate, labels, defaultScenario) {
  // Normalize data to match the object
  // shape used on the dynamic rates
  rate.planDescription ??= rate.description ?? '';
  rate.type ??= rate.loanType ?? '';
  rate.term ??= rate.loanTerm ?? '';
  rate.minPaymentAfter1stRateChange ??= rate.minPaymentAfterFirstRateChange;

  delete rate.description;
  delete rate.loanType;
  delete rate.loanTerm;
  delete rate.minPaymentAfterFirstRateChange;

  // Format data
  rate.type = rate.type.toLowerCase();
  rate.loanPurpose = rate.loanPurpose?.trim().toLowerCase();
  rate.isAdjustable = isAdjustableRate(rate.type);
  rate.isFixed = isFixedRate(rate.type);
  rate.rate = round(rate.rate, 3);
  rate.apr = round(rate.apr, 3);
  rate.payment = round(rate.payment, 2);
  rate.closingCosts = round(rate.closingCosts, 2);
  rate.term = parseFloat(rate.term);
  rate.months = parseFloat(rate.months ?? rate.term * 12);

  // Add loan details as separate object that
  // mirrors the shape of dynamic input fields
  rate.loanDetails = {
    creditScore: defaultScenario.creditScore,
    loanAmount: rate.loanAmount,
    homeValue: round(rate.loanAmount / (rate.loanToValue / 100)),
    loanPurpose: rate.loanPurpose,
    occupancyType: defaultScenario.occupancyType,
    propertyType: defaultScenario.propertyType,
    zipCode: defaultScenario.zipCode
  };

  const productLabels = labels[String(rate.productTypeID)];
  rate.productLabel = productLabels?.name.trim();

  if (rate.isAdjustable) {
    rate.changeFrequencyUnits = productLabels?.changeFrequencyUnits ?? 'years';

    rate.timePeriodOfInitialRate = parseInt(rate.timePeriodOfInitialRate);
    rate.rateChangeFrequency = parseInt(rate.rateChangeFrequency);
  }

  // Fallback in case product label not found
  if (!rate.productLabel) {
    if (rate.isFixed) {
      rate.productLabel = `${rate.term} year fixed rate`;
    } else if (rate.isAdjustable) {
      const r = new RegExp(`[^/]\\d/${rate.rateChangeFrequency}[a-z ]`, 'i');
      const match = rate.planDescription?.match(r);
      rate.productLabel = match ? `${match[0].trim()} adjustable rate` : null;
    }
  }

  if (
    !rate.term ||
    !rate.productLabel ||
    rate.apr === 0 ||
    rate.rate === 0 ||
    isNaN(rate.apr) ||
    isNaN(rate.rate) ||
    isNaN(rate.payment) ||
    isNaN(rate.closingCosts) ||
    (!rate.isAdjustable && !rate.isFixed)
  ) {
    console.warn('Invalid rack rate', rate);
    return null;
  }

  return rate;
}
